import { Descriptions, List, Space, Tabs, Tooltip } from "antd";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import { BaseEntityManager } from "../../components/entityManager";
import { enmControlType, enmPropertyType, IInitialData, IMetadata } from "../../components/entityManager/interfaces";
import AppContext from "../../components/infrastructure/appContext";
import { AndFilter, ComparisonFilter, enmConditionalOperators, IFilter } from "../../components/infrastructure/filtering";
import { Insurer } from "../insurer";
import { Policyholder } from "../policyholder";
import { IPolicy, IPolicyFetchingInfo, IPolicyProps, IPolicyStates } from "./interfaces";
import { Link } from "react-router-dom";
import { PdfViewer } from "../../components/pdfViewer";

const { TabPane } = Tabs;

export class Policy extends BaseEntityManager<IPolicy, IPolicyProps, IPolicyStates, IPolicyFetchingInfo, IInitialData>{
    static contextType = AppContext;
    static initMetadata = (): IMetadata => {
        return {
            name: "Policy",
            title: "بیمه نامه",
            Properties: [
                {
                    title: "بیمه گر",
                    dataIndex: "InsurerId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 200,
                    rules: [{ required: true, message: 'لطفا بیمه گر را وارد نمایید' }],
                    lookup: {
                        lookupComponent: Insurer,
                        lookupProperty: "Insurer",
                        lookupDisplayFields: ["Name"]
                    }
                },
                {
                    title: "بیمه گزار/مشتری",
                    dataIndex: "PolicyholderId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 200,
                    rules: [{ required: true, message: 'لطفا بیمه گزار/مشتری را وارد نمایید' }],
                    lookup: {
                        lookupComponent: Policyholder,
                        lookupProperty: "Policyholder",
                        lookupDisplayFields: ["NationalCode", "FullName"]
                    }
                },
                {
                    title: "سریال بیمه نامه",
                    dataIndex: "SerialNo",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا سریال بیمه نامه را وارد نمایید' }]
                },
                //{
                //    title: "شماره الحاقیه",
                //    dataIndex: "EndorseNo",
                //    propertyType: enmPropertyType.number,
                //    controlType: enmControlType.numberinput,
                //    filterable: true,
                //    width: 150,
                //    rules: [{ required: true, message: 'لطفا شماره الحاقیه را وارد نمایید' }]
                //},
                {
                    title: "نام طرح",
                    dataIndex: "PlanName",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150
                },
                {
                    title: "تاریخ شروع",
                    dataIndex: "StartDateTime",
                    propertyType: enmPropertyType.datetime,
                    controlType: enmControlType.datetime,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا تاریخ شروع بیمه نامه را وارد نمایید' }]
                },
                {
                    title: "تاریخ پایان",
                    dataIndex: "EndDateTime",
                    propertyType: enmPropertyType.datetime,
                    controlType: enmControlType.datetime,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا تاریخ پایان بیمه نامه را وارد نمایید' }]
                },
                {
                    dataIndex: "PolicyEquipmentModels.EquipmentSerialNo", // only for filter
                    controlType: enmControlType.textbox,
                    propertyType: enmPropertyType.string,
                    allowNull: true,
                    filterable: true,
                    showColumn: false,
                    showControl: false,
                    title: "سریال تجهیز",
                }
                //{
                //    title: "شماره شبا",
                //    dataIndex: "ShebaNo",
                //    propertyType: enmPropertyType.string,
                //    controlType: enmControlType.textbox,
                //    filterable: true,
                //    width: 150,
                //    rules: [{ required: true, message: 'لطفا شماره شبا را وارد نمایید' }],
                //    showColumn: false
                //},
            ]
        };
    }
    tabsElement?: HTMLDivElement | null;

    onInitializing() {

        this.searchArea = { columnsPerRow: 2 };
        this.searchable = true;

        this.originalFilter = () => {
            const { params } = this.context;
            let filters: IFilter[] = [];
            if (params.id) {
                filters.push(new ComparisonFilter("RowGuid", enmConditionalOperators.Equal, params.id));
            }
            if (filters.length > 0)
                return new AndFilter(filters);
        };

        this.onDataFetching = (fetchingInfo: IPolicyFetchingInfo, onViewMode) => {
            if (this.props.params) {
                const { minValidDate, loadPolicyEquipmentModels } = this.props.params();
                if (minValidDate)
                    fetchingInfo.MinValidDate = minValidDate;
                fetchingInfo.LoadPolicyEquipmentModels = loadPolicyEquipmentModels;
            }
            else
                fetchingInfo.LoadPolicyEquipmentModels = onViewMode;
            fetchingInfo.LoadNavigations = onViewMode;
            fetchingInfo.LoadPolicyAttachments = onViewMode;
            return fetchingInfo;
        }

        this.backNode = () => {
            const { params } = this.context;
            if (params.returnurl) {
                const { pathname, search } = new URL(window.location.origin + params.returnurl);
                return (
                    <Link to={{ pathname, search }} style={{ width: "180px", marginTop: 10, marginBottom: 10, marginLeft: 10 }}>
                        <Tooltip title="بازگشت" ><Space style={{ color: "darkblue" }}><FontAwesomeIcon key="back-icon" className="back-color" icon={faReply} transform={{ flipX: true }} size={'xl'} style={{ margin: 10 }} />{params.invokepath ?? ''}</Space></Tooltip>
                    </Link>
                );
            };
        };

        this.extraViewNode = () => {

            const { currentEntity } = this.state;

            let policyFranchiseAccess = this.initialData?.AccessList["PolicyFranchise"];
            let policyEquipmentModelAccess = this.initialData?.AccessList["PolicyEquipmentModel"];
            let policyCoverageAccess = this.initialData?.AccessList["PolicyCoverage"];


            if (!currentEntity || !(policyFranchiseAccess || policyEquipmentModelAccess || policyCoverageAccess))
                return;

            const policyCert = currentEntity?.PolicyAttachments[0];

            return (
                <div style={{ margin: 0, marginTop: 10 }} ref={node => this.tabsElement = node}>
                    <div className="box" style={{ border: "1px solid #91b5b7" }}>
                        <Tabs defaultActiveKey={policyCoverageAccess ? "1" : (policyEquipmentModelAccess ? "2" : "3")} style={{ maxWidth: "100%" }} onChange={key => {
                            if (this.tabsElement) {
                                this.setState({ policyCertFrameDisplayed: key === "4" });
                            }
                        }}
                        >
                            {policyCoverageAccess &&
                                <TabPane
                                    tab={
                                        <span style={{ marginRight: 4 }}>
                                            پوشش ها
                                        </span>
                                    }
                                    key="1"
                                    style={{ margin: 10 }}
                                >
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={currentEntity.PolicyCoverages.map(c => { return { title: c.CoverageTitle } })}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <Descriptions key={"PolicyCoverages"} column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}>
                                                            <Descriptions.Item label="عنوان">{item.title}</Descriptions.Item>
                                                        </Descriptions>
                                                    }
                                                    style={{ height: 25 }}
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </TabPane>
                            }
                            {policyEquipmentModelAccess &&
                                <TabPane
                                    tab={
                                        <span>
                                            تجهیزات بیمه شده
                                        </span>
                                    }
                                    key="2"
                                    style={{ margin: 10 }}
                                >
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={currentEntity.PolicyEquipmentModels.map(f => { return { brandTitle: f.BrandTitle, equipmentModelTitle: f.EquipmentModelTitle, equipmentSerialNo: f.EquipmentSerialNo, insuredValue: f.InsuredValue } })}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <Descriptions key={"PolicyFranchises"} column={{ xxl: 4, xl: 4, lg: 4, md: 2, sm: 1, xs: 1 }}>
                                                            <Descriptions.Item label="برند">{item.brandTitle}</Descriptions.Item>
                                                            <Descriptions.Item label="مدل">{item.equipmentModelTitle}</Descriptions.Item>
                                                            <Descriptions.Item label="سریال یا IMEI دستگاه">{item.equipmentSerialNo}</Descriptions.Item>
                                                            <Descriptions.Item label="سرمایه بیمه نامه">{`${item.insuredValue ?? 0} ریال`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Descriptions.Item>
                                                        </Descriptions>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </TabPane>
                            }
                            {policyFranchiseAccess &&
                                <TabPane
                                    tab={
                                        <span style={{ marginRight: 10 }}>
                                            فرانشیزها
                                        </span>
                                    }
                                    key="3"
                                    style={{ margin: 10 }}
                                >
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={currentEntity.PolicyFranchises.map(f => { return { title: f.FranchiseTitle, percent: f.FranchisePercent, minimumValue: f.MinimumValue } })}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    title={
                                                        <Descriptions key={"PolicyFranchises"} >
                                                            <Descriptions.Item className="col-sm-6" label="عنوان"><span >{item.title}</span></Descriptions.Item>
                                                            <Descriptions.Item className="col-sm-2" label="درصد">{item.percent}</Descriptions.Item>
                                                            <Descriptions.Item className="col-sm-4" label="حداقل مبلغ فرانشیز">{`${item.minimumValue ?? 0} ریال`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</Descriptions.Item>
                                                        </Descriptions>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </TabPane>

                            }
                            {
                                !!policyCert &&
                                <TabPane
                                    tab={
                                        <span style={{ marginRight: 4 }}>
                                            گواهی بیمه نامه
                                        </span>
                                    }
                                    key="4"
                                    style={{ margin: 10, minHeight: 600 }}
                                >
                                    <div style={{ width: 'calc(100% - 20px)' }}>
                                        {this.state.policyCertFrameDisplayed && <PdfViewer url={policyCert?.Path} onLoad={() => this.tabsElement?.scrollIntoView()} />}
                                    </div>
                                </TabPane>
                            }
                        </Tabs>
                    </div>
                </div>
            );
        };
    }
}